import { Text } from '@mantine/core';
import { modals } from '@mantine/modals';
import { ExternalMailAction } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailAction';
import { ExternalMailActionAccountManager } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionAccountManager';
import { ExternalMailActionDebtor } from 'app/Private/ExternalMailbox/ExternalMailActions/ExternalMailActionDebtor';
import { i18nKeys, tKeys, useTranslation } from 'locales';
import type { Thread } from 'modules/Mails/hooks';
import posthog from 'posthog-js';
import { useHistory } from 'react-router';
import { captureEvent, type MailActionOptions } from 'shared/analytics/capture-event';
import { useDeleteMail } from 'shared/hooks/use-delete-mail';
import { useUpdateThread } from 'shared/hooks/use-update-thread';

type UseMailDetailActionsOptions = {
  id: string;
  thread: Thread;
  refetchThread: () => void;
};

export const useMailDetailActions = ({
  id,
  thread,
  refetchThread,
}: UseMailDetailActionsOptions) => {
  const { t } = useTranslation();
  const history = useHistory();

  const { deleteMail, isDeleteMailLoading } = useDeleteMail();
  const { updateThread, isUpdateThreadLoading } = useUpdateThread();

  const handleToggleStatus = ({ trigger }: MailActionOptions) => {
    const isProcessed = !thread.isProcessed;

    updateThread(
      { id, isProcessed },
      {
        onSuccess: () => {
          captureEvent(posthog, 'mail:thread_status_toggled', {
            is_processed: isProcessed,
            trigger,
          });
          refetchThread();
        },
      },
    );
  };

  const handleAssign = ({ trigger }: MailActionOptions) => {
    captureEvent(posthog, 'mail:thread_assign_opened', { trigger });
    modals.open({
      title: t(i18nKeys.TASK.ACTIONS.ASSIGN),
      centered: true,
      children: (
        <ExternalMailAction
          mail={thread}
          onSubmit={data => {
            updateThread(
              { id, debtorId: data.debtorId, accountManagerId: data.userId },
              { onSuccess: () => modals.closeAll() },
            );
          }}
        >
          <ExternalMailActionDebtor />
          <ExternalMailActionAccountManager />
        </ExternalMailAction>
      ),
    });
  };

  const handleDelete = () => {
    modals.openConfirmModal({
      title: t(tKeys.CONFIRM),
      centered: true,
      children: <Text>{t(tKeys.AYS.DELETE_EXTERNAL_MAIL)}</Text>,
      labels: { confirm: t(tKeys.DELETE), cancel: t(tKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteMail({ id }, { onSuccess: () => history.push('/mails') }),
    });
  };

  return {
    handleToggleStatus,
    handleAssign,
    handleDelete,
    isDeleteMailLoading,
    isUpdateThreadLoading,
  };
};
